import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  CONFIRM_DIALOG_MAX_WIDTH,
  FOOTER_PY,
  MODAL_PX,
  headerSx,
  primaryButtonSx,
  sectionTitleSx,
  subtitleSx,
  titleSx,
} from '../campaigns/applicationNegotiationTheme';
import { navigateToProfileCompletion } from '../../utils/shared/profileCompletionNavigation';
import { getFieldDisplayName } from '../../utils/shared/profileCompletion';
import { FONT_SIZES } from '../../Theme';

const VISIBLE_MISSING_FIELD_LIMIT = 8;

function getMoreFieldsLabel(extraCount) {
  const noun = extraCount > 1 ? 'fields' : 'field';
  return `and ${extraCount} more ${noun}...`;
}

/**
 * Blocking dialog shown when a brand or influencer profile is still incomplete.
 */
function ProfileCompletionModal({
  open,
  userType,
  missingFields = [],
  completionPercentage = 0,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const userTypeLabel = userType === 'influencer' ? 'influencer' : 'brand';
  const visibleMissingFields = missingFields.slice(0, VISIBLE_MISSING_FIELD_LIMIT);
  const hiddenFieldCount = missingFields.length - visibleMissingFields.length;

  const handleCompleteProfile = () => {
    navigateToProfileCompletion(navigate, userType, missingFields);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      maxWidth={false}
      aria-labelledby="profile-completion-title"
      aria-describedby="profile-completion-description"
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          margin: 2,
          width: '100%',
          maxWidth: CONFIRM_DIALOG_MAX_WIDTH,
          boxShadow: theme.palette.chat?.windowShadow || theme.shadows[8],
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(theme.palette.text.primary, 0.48),
        },
      }}
    >
      <Box sx={headerSx}>
        <Typography id="profile-completion-title" component="h2" sx={titleSx}>
          Finish your application
        </Typography>
      </Box>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 2 }}>
        <Typography id="profile-completion-description" sx={subtitleSx}>
          {`Your ${userTypeLabel} profile is ${completionPercentage}% complete. Add the rest to access Millet.`}
        </Typography>

        {completionPercentage > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
              <Typography sx={{ fontSize: FONT_SIZES.bodySm, color: 'text.secondary' }}>
                Profile
              </Typography>
              <Typography
                sx={{
                  fontSize: FONT_SIZES.bodySm,
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {`${completionPercentage}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              aria-label={`Profile ${completionPercentage}% complete`}
              sx={{
                height: 5,
                borderRadius: 999,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: 'text.primary',
                },
              }}
            />
          </Box>
        )}

        {missingFields.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ ...sectionTitleSx, mb: 0.75 }}>
              Still needed
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                '& li': {
                  mb: 0.5,
                  fontSize: FONT_SIZES.bodySm,
                  color: 'text.secondary',
                },
              }}
            >
              {visibleMissingFields.map((field) => (
                <li key={field}>
                  {getFieldDisplayName(field)}
                </li>
              ))}
              {hiddenFieldCount > 0 && (
                <li>{getMoreFieldsLabel(hiddenFieldCount)}</li>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: MODAL_PX,
          pt: 1.5,
          pb: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', sm: FOOTER_PY },
          justifyContent: 'flex-end',
        }}
        disableSpacing
      >
        <Button
          variant="contained"
          onClick={handleCompleteProfile}
          sx={primaryButtonSx(theme)}
        >
          Complete application
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProfileCompletionModal;
